import { CheckboxFieldUI } from '@axo/form/feature/form-input/CheckboxField/CheckboxFieldUI';
import { DateFieldUI } from '@axo/form/feature/form-input/DateField/DateFieldUI';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { useWatch } from 'react-hook-form';
import { getResidencePermitOptions } from '../../../../../utils/Denmark/getResidencePermitOptions';
import type { CitizenFormGroupData } from '../../FormData.dk.types';
import {
  Citizenship,
  ResidencePermit,
  citizenshipOptions,
  residencePermitOptions,
} from '../../config/globals';
import { requiredField, requiredUnregisterField } from '../shared-config';

export function CitizenFormGroup() {
  const { t } = useTranslation();

  const [isDanishCitizen, citizenship, _typeOfResidence] = useWatch({
    name: [
      'Frontend/IsDanish',
      'LoanApplication/Citizenship',
      'LoanApplication/ResidencePermit',
    ],
  });

  const typeOfResidence = Number(_typeOfResidence);

  const askResidence = [Citizenship.EU, Citizenship.Other].includes(
    citizenship
  );

  const askResidenceDetails = [
    ResidencePermit.Permanent,
    ResidencePermit.Temporary,
  ].includes(typeOfResidence);

  const askResidenceExpirationDate =
    typeOfResidence === ResidencePermit.Temporary;

  const currentYear = new Date().getFullYear();

  return (
    <>
      <CheckboxFieldUI<CitizenFormGroupData>
        name="Frontend/IsDanish"
        data-testid="is-danish-toggle"
      >
        {t('IsDanish.label')}
      </CheckboxFieldUI>

      {!isDanishCitizen && (
        <>
          <DropdownFieldUI<CitizenFormGroupData>
            name="LoanApplication/Citizenship"
            label={t('Citizenship.label')}
            placeholder={t('placeholder')}
            data-testid="citizenship-dropdown"
            options={citizenshipOptions}
            registerOptions={requiredField}
          />

          {askResidence && (
            <DropdownFieldUI<CitizenFormGroupData>
              name="LoanApplication/ResidencePermit"
              label={t('ResidencePermit.label')}
              placeholder={t('placeholder')}
              data-testid="residence-permit-dropdown"
              options={residencePermitOptions}
              registerOptions={requiredUnregisterField}
            />
          )}

          {askResidenceDetails && (
            <>
              <DropdownFieldUI<CitizenFormGroupData>
                name="LoanApplication/ResidencePermitType"
                label={t('TypeOfStay.label', {
                  residence: t(
                    `ResidencePermit.${ResidencePermit[typeOfResidence]}`
                  ),
                })}
                placeholder={t('placeholder')}
                data-testid="residence-permit-type-dropdown"
                options={getResidencePermitOptions(typeOfResidence)}
                registerOptions={requiredUnregisterField}
              />

              <NumberFieldUI<CitizenFormGroupData>
                name="LoanApplication/ResidencePermitNumber"
                label={t('ResidencePermitNumber.label')}
                data-testid="residence-permit-number-input"
                registerOptions={requiredUnregisterField}
              />
            </>
          )}

          {askResidenceExpirationDate && (
            <DateFieldUI<CitizenFormGroupData>
              name="LoanApplication/ResidencePermitExpiration"
              label={t('ResidenceExpire.label')}
              data-testid="residence-permit-expiration-dropdown"
              yearPlaceholder={t('placeholder')}
              i18n="da-DK"
              minYear={currentYear}
              maxYear={currentYear + 30}
              latestYearAtTop={true}
              disableDatesInPast
              rules={requiredUnregisterField}
            />
          )}
        </>
      )}
    </>
  );
}
